export type SystemBalanceTarget = "company" | "buyer" | "card"
export type SystemBalanceDirection = "in" | "out"
export type SystemUserAction = "disable" | "unblock" | "hard-block"

export interface SystemMutationRequest {
	path: string
	body: Record<string, string | number>
}

export const createDbBalanceSetRequest = (
	target: SystemBalanceTarget,
	uid: string,
	amount: number,
	teamUid?: string
): SystemMutationRequest => {
	if (target === "company") {
		return {
			path: "system/company/balance/set",
			body: { company_uid: uid, amount }
		}
	}
	if (target === "buyer") {
		return {
			path: "system/user/balance/set",
			body: {
				user_uid: uid,
				...(teamUid ? { team_uid: teamUid } : {}),
				amount
			}
		}
	}
	return {
		path: "system/cards/balance/set",
		body: { card_uid: uid, amount }
	}
}

export const createTrxBalanceAdjustmentRequest = (
	target: SystemBalanceTarget,
	direction: SystemBalanceDirection,
	uid: string,
	amount: number,
	currency: string,
	issuer: number
): SystemMutationRequest => ({
	path: `system/${target}/balance/adjust/${direction}`,
	body: { uid, amount, currency, issuer }
})

export const createCardBlockRequest = (
	cardUid: string,
	destination: "buyer" | "company"
): SystemMutationRequest => ({
	path: "system/cards/block",
	body: { card_uid: cardUid, destination }
})

export const createUserStateRequest = (
	action: SystemUserAction,
	userUid: string
): SystemMutationRequest => {
	if (action === "disable") {
		return { path: "system/user/disable", body: { user_uid: userUid } }
	}
	if (action === "unblock") {
		return {
			path: "system/users/unblock",
			body: { user_uid: userUid, unfreeze_cards: "true" }
		}
	}
	return {
		path: "system/users/block",
		body: { user_uid: userUid, mode: "block" }
	}
}

export const isSettledDifference = (value: number | null | undefined, tolerance = 1) =>
	typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= tolerance

export const formatMinorCurrency = (value: number | null | undefined, currency: string) => {
	if (value === null || value === undefined || !Number.isFinite(Number(value))) {
		return "Unavailable"
	}
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(Number(value) / 100)
}
